import {Injectable} from '@angular/core';
import {ManagedLocaleService} from './managed-locale.service';
import * as numeral from 'numeral';
import {UserContextService} from '../app-context/user-context/user-context.service';

@Injectable({
  providedIn: 'root'
})
export class LanguageService {

  private static configuration = {
    fr: {
      delimiters: {
        thousands: ' ',
        decimal: ','
      },
      abbreviations: {
        thousand: 'k',
        million: 'M',
        billion: 'Md',
        trillion: 'Bn',
        year: 'a',
        quarter: 'T',
        month: 'M'
      },
      ordinal: (nb: number) => {
        return nb === 1 ? 'er' : 'e';
      },
      currency: {
        symbol: '€'
      }
    },
    en: {
      delimiters: {
        thousands: ',',
        decimal: '.'
      },
      abbreviations: {
        thousand: 'k',
        million: 'm',
        billion: 'b',
        trillion: 't',
        year: 'Y',
        quarter: 'Q',
        month: 'M'
      },
      ordinal: (nb: number) => {
        const b = nb % 10;
        return ((nb % 100 / 10) === 1) ? 'th' :
          (b === 1) ? 'st' :
            (b === 2) ? 'nd' :
              (b === 3) ? 'rd' : 'th';
      },
      currency: {
        symbol: '$'
      }
    },
    de: {
      delimiters: {
        thousands: '.',
        decimal: ','
      },
      abbreviations: {
        thousand: 'k',
        million: 'm',
        billion: 'b',
        trillion: 't',
        year: 'J',
        quarter: 'Q',
        month: 'M'
      },
      ordinal: () => {
        return '.';
      },
      currency: {
        symbol: '€'
      }
    },
    it: {
      delimiters: {
        thousands: '.',
        decimal: ','
      },
      abbreviations: {
        thousand: 'mila',
        million: 'mil',
        billion: 'b',
        trillion: 't',
        year: 'A',
        quarter: 'T',
        month: 'M'
      },
      ordinal: () => {
        return 'º';
      },
      currency: {
        symbol: '€'
      }
    },
    es: {
      delimiters: {
        thousands: '.',
        decimal: ','
      },
      abbreviations: {
        thousand: 'k',
        million: 'mm',
        billion: 'b',
        trillion: 't',
        year: 'A',
        quarter: 'T',
        month: 'M'
      },
      ordinal: (nb: number) => {
        const b = nb % 10;
        return (b === 1 || b === 3) ? 'er' :
          (b === 2) ? 'do' :
            (b === 7 || b === 0) ? 'mo' :
              (b === 8) ? 'vo' :
                (b === 9) ? 'no' : 'to';
      },
      currency: {
        symbol: '€'
      }
    },
    nl: {
      delimiters: {
        thousands: '.',
        decimal: ','
      },
      abbreviations: {
        thousand: 'k',
        million: 'mln',
        billion: 'mld',
        trillion: 'bln',
        year: 'J',
        quarter: 'T',
        month: 'M'
      },
      ordinal: (nb: number) => {
        const remainder = nb % 100;
        return (nb !== 0 && remainder <= 1 || remainder === 8 || remainder >= 20) ? 'ste' : 'de';
      },
      currency: {
        symbol: '€'
      }
    }
  };

  constructor(private userContextService: UserContextService) {
    ManagedLocaleService.MANAGED_LOCALES.forEach(locale => {
      if (this.isLocaleNotRegisteredAndAvailable(locale)) {
        this.getNumeral().register('locale', locale, LanguageService.configuration[locale]);
      }
    });
    // init default language
    this.getNumeral().locale(userContextService.getUserContext().locale);
  }

  public getDecimalSeparator(): string {
    return this.getNumeral().localeData(this.userContextService.getUserContext().locale).delimiters.decimal;
  }

  public getThousandSeparator(): string {
    return this.getNumeral().localeData(this.userContextService.getUserContext().locale).delimiters.thousands;
  }

  public getCurrency(): string {
    return this.getNumeral().localeData(this.userContextService.getUserContext().locale).currency.symbol;
  }

  public getNumeral(): any {
    return numeral;
  }

  // public nowInLocalDateString(): string {
  //   return moment().format('YYYY-MM-DD');
  // }

  private isLocaleNotRegisteredAndAvailable(locale): boolean {
    return locale !== 'en' // 'en' is already registered
      && LanguageService.configuration[locale]
      && this.getNumeral().locales[locale] === undefined;
  }
}
